import { useEffect, useState, type ComponentType } from "react";
import { Phone, ChevronRight, ChevronDown } from "lucide-react";
import ServiceHeader from "@/components/ServiceHeader";
import Footer from "@/components/Footer";
import StickyCallButton from "@/components/StickyCallButton";

export type ServiceSlug =
  | "home-moves"
  | "apartment-moves"
  | "office-relocations"
  | "single-item-moves"
  | "same-day-moves";

export interface ServiceLayoutProps {
  slug: string;
  title: string;
  metaDescription: string;
  h1: string;
  subtitle: string;
  breadcrumbLabel?: string;
  breadcrumbParent?: { label: string; href: string };
  aboutHeading?: string;
  overview: string[];
  includedHeading?: string;
  included: { icon: ComponentType<{ className?: string }>; label: string }[];
  whyUsHeading?: string;
  whyUs: { icon: ComponentType<{ className?: string }>; title: string; desc: string }[];
  faqs: { q: string; a: string }[];
  schemaServiceType: string;
  areaServed?: string[];
  extra?: React.ReactNode;
}

const processSteps = [
  { n: "1", t: "Request a Quote", d: "Send us the details or call - we reply fast." },
  { n: "2", t: "We Confirm Your Booking", d: "Lock in a date, time and crew that suit you." },
  { n: "3", t: "Moving Day", d: "Our team arrives on time, prepped and ready." },
  { n: "4", t: "Unload & Position", d: "Careful placement in your new space." },
  { n: "5", t: "Final Inspection", d: "We walk through with you before we leave." },
];

const upsertMeta = (
  kind: "name" | "property",
  key: string,
  content: string,
): { restore: () => void } => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${kind}="${key}"]`);
  const created = !el;
  const prev = el?.getAttribute("content") ?? "";
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return {
    restore: () => {
      if (created) el?.remove();
      else el?.setAttribute("content", prev);
    },
  };
};

const upsertLdJson = (id: string, data: unknown) => {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  const created = !el;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return () => {
    if (created) el?.remove();
  };
};

const glass =
  "bg-white/70 backdrop-blur-xl border border-white/60 shadow-[0_8px_30px_rgba(26,26,26,0.06)]";

const ServicePageLayout = (props: ServiceLayoutProps) => {
  const url = `https://ozmoveco.com.au/${props.slug}`;
  const parent = props.breadcrumbParent ?? { label: "Services", href: "/#services" };

  useEffect(() => {
    const prevTitle = document.title;
    document.title = props.title;

    const cleanups: Array<() => void> = [];
    cleanups.push(upsertMeta("name", "description", props.metaDescription).restore);
    cleanups.push(upsertMeta("property", "og:title", props.title).restore);
    cleanups.push(upsertMeta("property", "og:description", props.metaDescription).restore);
    cleanups.push(upsertMeta("property", "og:url", url).restore);
    cleanups.push(upsertMeta("name", "twitter:title", props.title).restore);
    cleanups.push(upsertMeta("name", "twitter:description", props.metaDescription).restore);

    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href") ?? "";
    canonical?.setAttribute("href", url);

    const cleanService = upsertLdJson("ld-service", {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: props.schemaServiceType,
      name: props.h1,
      description: props.metaDescription,
      url,
      provider: {
        "@type": "MovingCompany",
        name: "OZ MOVE CO.",
        url: "https://ozmoveco.com.au/",
        telephone: "+61-1800-556-654",
        areaServed: "AU",
      },
      areaServed: (props.areaServed ?? ["Melbourne"]).map((s) => ({ "@type": "Place", name: s })),
    });
    const cleanBreadcrumb = upsertLdJson("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://ozmoveco.com.au/" },
        { "@type": "ListItem", position: 2, name: parent.label, item: `https://ozmoveco.com.au${parent.href.replace(/^\//, "/")}` },
        { "@type": "ListItem", position: 3, name: props.h1, item: url },
      ],
    });
    const cleanFaq = upsertLdJson("ld-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: props.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
      if (canonical) canonical.setAttribute("href", prevCanonical);
      cleanService();
      cleanBreadcrumb();
      cleanFaq();
    };
  }, [props, url, parent.href, parent.label]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ServiceHeader />

      <main className="flex-1">
        {/* Minimal hero */}
        <section className="relative overflow-hidden bg-[#FFC300]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.55),transparent_55%)]" aria-hidden="true" />
          <div className="container relative py-16 md:py-28">
            <nav aria-label="Breadcrumb" className="mb-8 text-sm text-[#1a1a1a]/70 flex flex-wrap items-center gap-1">
              <a href="/" className="hover:text-[#1a1a1a] font-semibold">Home</a>
              <ChevronRight className="w-4 h-4 opacity-50" />
              <a href={parent.href} className="hover:text-[#1a1a1a] font-semibold">{parent.label}</a>
              <ChevronRight className="w-4 h-4 opacity-50" />
              <span className="text-[#1a1a1a] font-bold">{props.breadcrumbLabel ?? props.h1}</span>
            </nav>

            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-black text-[#1a1a1a] leading-[1.05] tracking-tight">
                {props.h1}
              </h1>
              <p className="mt-6 text-lg md:text-2xl text-[#1a1a1a]/75 font-medium leading-relaxed">
                {props.subtitle}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  href="/#quote"
                  className="bg-[#1a1a1a] text-[#FFC300] px-8 py-4 text-base md:text-lg font-bold rounded-xl hover:opacity-90 transition-opacity text-center"
                >
                  Get Free Quote
                </a>
                <a
                  href="tel:1800556654"
                  data-call-source="service_hero"
                  aria-label="Call 1800 556 654"
                  className="inline-flex items-center justify-center gap-2 bg-white/60 backdrop-blur-xl border border-[#1a1a1a]/15 text-[#1a1a1a] px-8 py-4 text-base md:text-lg font-bold rounded-xl hover:bg-white/80 transition-colors"
                >
                  <Phone className="w-5 h-5" /> Call Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* About */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] mb-8 tracking-tight">
              {props.aboutHeading ?? "About This Service"}
            </h2>
            <div className="space-y-5 text-[#1a1a1a]/75 text-base md:text-lg leading-relaxed">
              {props.overview.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 md:py-28 bg-[#f7f7f7]">
          <div className="container">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-14 tracking-tight">
              {props.includedHeading ?? "What's Included"}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
              {props.included.map((item) => (
                <div key={item.label} className={`${glass} rounded-2xl p-5 flex items-start gap-3`}>
                  <div className="w-10 h-10 shrink-0 bg-[#FFC300] rounded-full flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-[#1a1a1a]" />
                  </div>
                  <span className="text-[#1a1a1a] font-semibold pt-1.5 leading-snug">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 md:py-28 bg-[#1a1a1a]">
          <div className="container">
            <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-14 tracking-tight">
              {props.whyUsHeading ?? "Why Choose OZ MOVE CO."}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {props.whyUs.map((f) => (
                <div
                  key={f.title}
                  className="bg-white/[0.07] backdrop-blur-xl border border-white/10 rounded-2xl p-6"
                >
                  <div className="w-11 h-11 bg-[#FFC300] rounded-full flex items-center justify-center mb-4">
                    <f.icon className="w-5 h-5 text-[#1a1a1a]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {props.extra}

        {/* Process */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-14 tracking-tight">
              Our Process
            </h2>

            <div className="hidden lg:block relative max-w-6xl mx-auto">
              <div className="absolute top-8 left-8 right-8 h-0.5 bg-[#FFC300]" aria-hidden="true" />
              <div className="grid grid-cols-5 gap-4 relative">
                {processSteps.map((s) => (
                  <div key={s.n} className="text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-[#1a1a1a] text-[#FFC300] font-black text-xl flex items-center justify-center relative z-10 border-4 border-white">
                      {s.n}
                    </div>
                    <h3 className="mt-4 font-bold text-[#1a1a1a]">{s.t}</h3>
                    <p className="mt-1 text-sm text-[#1a1a1a]/70 leading-snug">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>

            <ol className="lg:hidden max-w-md mx-auto space-y-5">
              {processSteps.map((s) => (
                <li key={s.n} className="flex gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-[#1a1a1a] text-[#FFC300] font-black flex items-center justify-center">
                    {s.n}
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1a1a1a]">{s.t}</h3>
                    <p className="text-sm text-[#1a1a1a]/70">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-[#f7f7f7]">
          <div className="container max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-12 tracking-tight">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {props.faqs.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 md:py-28 bg-[#FFC300]">
          <div className="container max-w-3xl text-center">
            <h2 className="text-4xl md:text-5xl font-black text-[#1a1a1a] mb-5 tracking-tight">
              Moving Soon?
            </h2>
            <p className="text-lg md:text-xl text-[#1a1a1a]/75 mb-10 leading-relaxed">
              Get a free, no-obligation quote from a team that shows up on time and treats your belongings like their own.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#quote"
                className="bg-[#1a1a1a] text-[#FFC300] px-8 py-4 text-lg font-bold rounded-xl hover:opacity-90 transition-opacity"
              >
                Get Free Quote
              </a>
              <a
                href="tel:1800556654"
                data-call-source="service_final_cta"
                aria-label="Call 1800 556 654"
                className="inline-flex items-center justify-center gap-2 bg-white/60 backdrop-blur-xl border border-[#1a1a1a]/15 text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:bg-white/80 transition-colors"
              >
                <Phone className="w-5 h-5" /> Call Now
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${glass} rounded-2xl overflow-hidden`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="font-bold text-[#1a1a1a]">{q}</span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-[#1a1a1a] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-5 pb-5 text-[#1a1a1a]/75 leading-relaxed">{a}</div>}
    </div>
  );
};

export default ServicePageLayout;
